import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Stack,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useApi } from '../api/client';
import { Page } from '../components/Page';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyIcon from '@mui/icons-material/Key';

export default function Attendance() {
  const { request } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [clockInLoading, setClockInLoading] = useState(false);
  const [clockOutLoading, setClockOutLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [kekaTokenValue, setKekaTokenValue] = useState('');
  const [kekaTokenLoading, setKekaTokenLoading] = useState(false);
  const [kekaTokenMessage, setKekaTokenMessage] = useState(null);
  const [validateTokenLoading, setValidateTokenLoading] = useState(false);
  const [validateTokenResult, setValidateTokenResult] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const attendanceRes = await request('/api/attendance', { method: 'GET' });
      if (attendanceRes.ok) {
        const json = await attendanceRes.json();
        setData(json);
      }
    } catch (e) {
      console.error('Failed to load attendance', e);
    } finally {
      setLoading(false);
    }
  }

  async function setCronsEnabled(enabled) {
    setToggling(true);
    setActionError(null);
    try {
      const res = await request('/api/attendance', {
        method: 'PATCH',
        body: JSON.stringify({ cronsEnabled: enabled }),
      });
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (e) {
      console.error('Failed to update attendance', e);
    } finally {
      setToggling(false);
    }
  }

  async function manualCheckIn() {
    setClockInLoading(true);
    setActionError(null);
    try {
      const res = await request('/api/attendance/check-in', { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      if (json.todayCheckIn !== undefined) setData(json);
      if (!res.ok) setActionError(json?.error || 'Check-in failed');
    } catch (e) {
      setActionError(e.message || 'Check-in failed');
    } finally {
      setClockInLoading(false);
    }
  }

  async function manualCheckOut() {
    setClockOutLoading(true);
    setActionError(null);
    try {
      const res = await request('/api/attendance/check-out', { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      if (json.todayCheckOut !== undefined) setData(json);
      if (!res.ok) setActionError(json?.error || 'Check-out failed');
    } catch (e) {
      setActionError(e.message || 'Check-out failed');
    } finally {
      setClockOutLoading(false);
    }
  }

  async function validateAccessToken() {
    setValidateTokenLoading(true);
    setValidateTokenResult(null);
    try {
      const res = await request('/api/attendance/validate-token', { method: 'GET' });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.valid) {
        setValidateTokenResult({ valid: true });
      } else {
        setValidateTokenResult({ valid: false, error: json?.error || 'Validation failed' });
      }
    } catch (e) {
      setValidateTokenResult({ valid: false, error: e.message || 'Validation failed' });
    } finally {
      setValidateTokenLoading(false);
    }
  }

  async function updateKekaRefreshToken() {
    setKekaTokenLoading(true);
    setKekaTokenMessage(null);
    try {
      const res = await request('/api/attendance/keka-refresh-token', {
        method: 'PUT',
        body: JSON.stringify({ refreshToken: kekaTokenValue.trim() }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        setKekaTokenMessage({ type: 'success', text: 'Keka refresh token updated successfully.' });
        setKekaTokenValue('');
      } else {
        setKekaTokenMessage({
          type: 'error',
          text: json?.message || json?.error || 'Failed to update token',
        });
      }
    } catch (e) {
      setKekaTokenMessage({ type: 'error', text: e.message || 'Failed to update token' });
    } finally {
      setKekaTokenLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  if (loading && !data) {
    return (
      <Page>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress />
        </Paper>
      </Page>
    );
  }

  return (
    <Page>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={1} sx={{ mb: 2 }}>
              <Typography variant="h6">Keka Attendance</Typography>
              <Button onClick={load} variant="outlined" size="small" startIcon={<RefreshIcon />} disabled={loading}>
                Refresh
              </Button>
            </Stack>

          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={data?.cronsEnabled !== false}
                    onChange={(e) => setCronsEnabled(e.target.checked)}
                    disabled={toggling}
                  />
                }
                label="Enable check-in / check-out crons (9:30 & 17:30 IST)"
              />
            </Stack>

            {actionError && (
              <Typography variant="body2" color="error">
                {actionError}
              </Typography>
            )}

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Stack direction="row" alignItems="center" spacing={1}>
                <LoginIcon color="action" />
                <Typography variant="body1">Today check-in:</Typography>
                {data?.todayCheckIn ? (
                  <Chip label="Done" color="success" size="small" />
                ) : (
                  <Chip label="Pending" color="default" size="small" />
                )}
                {data?.lastCheckInAt && (
                  <Typography variant="caption" color="text.secondary">
                    {new Date(data.lastCheckInAt).toLocaleString()}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={clockInLoading ? <CircularProgress size={16} /> : <LoginIcon />}
                  onClick={manualCheckIn}
                  disabled={clockInLoading || data?.todayCheckIn}
                >
                  Clock in
                </Button>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <LogoutIcon color="action" />
                <Typography variant="body1">Today check-out:</Typography>
                {data?.todayCheckOut ? (
                  <Chip label="Done" color="success" size="small" />
                ) : (
                  <Chip label="Pending" color="default" size="small" />
                )}
                {data?.lastCheckOutAt && (
                  <Typography variant="caption" color="text.secondary">
                    {new Date(data.lastCheckOutAt).toLocaleString()}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={clockOutLoading ? <CircularProgress size={16} /> : <LogoutIcon />}
                  onClick={manualCheckOut}
                  disabled={clockOutLoading || data?.todayCheckOut}
                >
                  Clock out
                </Button>
              </Stack>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Crons run only on weekdays (excluding holidays). Token refreshes daily at 9:00 IST. Check-in and check-out
              are delayed by 2–5 minutes after the scheduled time.
            </Typography>

            {data?.holidays?.month && (
              <Stack spacing={1} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Holidays ({data.holidays.month})
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
                  {(data.holidays.data || []).map((h) => (
                    <Chip
                      key={h.id != null ? h.id : h.date + (h.name || '')}
                      label={`${h.date}${h.name ? ` — ${h.name}` : ''}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                  {(data.holidays.data || []).length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      No holidays this month
                    </Typography>
                  )}
                </Stack>
              </Stack>
            )}

            <Accordion variant="outlined" sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <KeyIcon color="action" fontSize="small" />
                  <Typography variant="subtitle2">Update Keka refresh token</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={validateAccessToken}
                      disabled={validateTokenLoading}
                      startIcon={validateTokenLoading ? <CircularProgress size={18} /> : null}
                    >
                      {validateTokenLoading ? 'Validating…' : 'Validate access token'}
                    </Button>
                    {validateTokenResult && (
                      <Typography variant="body2" color={validateTokenResult.valid ? 'success.main' : 'error'}>
                        {validateTokenResult.valid ? 'Valid' : `Invalid: ${validateTokenResult.error}`}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Paste a new Keka refresh token to re-enable check-in/check-out when the current token expires.
                  </Typography>
                  <TextField
                    label="Keka refresh token"
                    type="password"
                    value={kekaTokenValue}
                    onChange={(e) => setKekaTokenValue(e.target.value)}
                    placeholder="Paste token from Keka / DevTools"
                    fullWidth
                    size="small"
                    autoComplete="off"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <KeyIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {kekaTokenMessage && (
                    <Typography variant="body2" color={kekaTokenMessage.type === 'success' ? 'success.main' : 'error'}>
                      {kekaTokenMessage.text}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    onClick={updateKekaRefreshToken}
                    disabled={!kekaTokenValue.trim() || kekaTokenLoading}
                    startIcon={kekaTokenLoading ? <CircularProgress size={18} /> : <KeyIcon />}
                  >
                    {kekaTokenLoading ? 'Updating…' : 'Update token'}
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
    </Page>
  );
}
